/**
 * PoC Generation Dialog Component
 * Generates and allows editing of Plan of Care document with AI
 */

import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Divider,
  CircularProgress,
  Stack,
  Alert,
  Fade,
} from '@mui/material'
import {
  Close as CloseIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material'
import { Session } from '@/types/contracts'
import { pocApi } from '@/services/pocApi'
import { htmlToMarkdown, markdownToHtml } from '@/utils/markdown'
import { NoteDemographicsHeader } from './NoteDemographicsHeader'
import SignatureCanvas from 'react-signature-canvas'

interface PoCGenerationDialogProps {
  open: boolean
  onClose: () => void
  session: Session
  contractId: string
  contract?: any
  onSuccess: (message?: string) => void
  onError?: (message: string) => void
  existingPoC?: {
    _id: string
    poc_doc: string
    poc_status: string
    signature?: string
  } | null
}

// Typing effect for AI-generated content
const typeText = async (
  text: string,
  setContent: React.Dispatch<React.SetStateAction<string>>,
  textareaRef: React.RefObject<HTMLTextAreaElement>
) => {
  setContent('')
  const words = text.split(' ')

  for (let i = 0; i < words.length; i++) {
    await new Promise(resolve => setTimeout(resolve, 30)) // Adjust speed here
    setContent(prev => {
      const newText = prev + (prev ? ' ' : '') + words[i]
      return newText
    })

    // Auto-scroll to bottom of textarea
    if (textareaRef.current) {
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight
    }
  }
}

const PoCGenerationDialog: React.FC<PoCGenerationDialogProps> = ({
  open,
  onClose,
  session,
  contractId,
  contract,
  onSuccess,
  onError,
  existingPoC,
}) => {
  const [pocContent, setPocContent] = useState('') // Store as markdown for internal state
  const [isGenerating, setIsGenerating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [error, setError] = useState('')
  const [signatureData, setSignatureData] = useState<string | null>(null)
  const pocInputRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null) // Keep for typeText function
  const signaturePadRef = useRef<SignatureCanvas>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const isGeneratingRef = useRef(false)
  const isUserEditingRef = useRef(false)
  const renderedPocHtml = useMemo(
    () => markdownToHtml(pocContent),
    [pocContent]
  )

  // Initialize signature if existing PoC has one
  useEffect(() => {
    if (existingPoC?.signature) {
      setSignatureData(existingPoC.signature)
    } else {
      setSignatureData(null)
    }
  }, [existingPoC])

  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear()
      setSignatureData(null)
    }
  }

  const handleSignatureEnd = () => {
    if (signaturePadRef.current) {
      try {
        if (!signaturePadRef.current.isEmpty()) {
          const dataURL = signaturePadRef.current.getTrimmedCanvas().toDataURL('image/png')
          setSignatureData(dataURL)
        }
      } catch (e) {
        console.error('Failed to get trimmed canvas:', e)
        try {
          // Fallback to normal canvas if trimmed fails
          setSignatureData(signaturePadRef.current.toDataURL('image/png'))
        } catch (e2) {
          // Failsafe so the user can still bypass if drawing occurred
          setSignatureData('signed_failsafe')
        }
      }
    } else {
      setSignatureData(null)
    }
  }

  // Populate contentEditable when switching to edit mode
  useEffect(() => {
    if (isEditing && pocInputRef.current && !isUserEditingRef.current) {
      const currentContent = pocInputRef.current.innerHTML.trim()
      const expectedContent = renderedPocHtml || ''
      // Only populate if empty or different
      if (!currentContent || currentContent !== expectedContent.trim()) {
        pocInputRef.current.innerHTML = expectedContent
      }
    }
  }, [isEditing, renderedPocHtml])

  // Update contentEditable div when rendered HTML changes (but not when user is editing)
  useEffect(() => {
    if (
      !pocInputRef.current ||
      isUserEditingRef.current ||
      isGenerating ||
      !isEditing
    )
      return

    const currentContent = pocInputRef.current.innerHTML.trim()
    const newContent = renderedPocHtml || ''

    if (currentContent !== newContent.trim() && newContent) {
      pocInputRef.current.innerHTML = newContent
    }
  }, [renderedPocHtml, isGenerating, isEditing])

  // Populate contentEditable when content finishes loading
  useEffect(() => {
    if (
      !isGenerating &&
      pocContent &&
      pocInputRef.current &&
      !isUserEditingRef.current &&
      isEditing
    ) {
      const currentContent = pocInputRef.current.innerHTML.trim()
      const expectedContent = renderedPocHtml || ''
      if (expectedContent && currentContent !== expectedContent.trim()) {
        pocInputRef.current.innerHTML = expectedContent
      }
    }
  }, [isGenerating, pocContent, renderedPocHtml, isEditing])

  // Load existing PoC or generate new one when dialog opens
  useEffect(() => {
    if (open) {
      if (existingPoC) {
        setPocContent(htmlToMarkdown(existingPoC.poc_doc))
        setIsEditing(true) // Start in edit mode for existing PoC
      } else if (!pocContent && !isGeneratingRef.current) {
        generatePoC()
      }
    }

    // Cleanup: abort any ongoing requests when dialog closes or component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
      isGeneratingRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingPoC])

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      // Abort any ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
      isGeneratingRef.current = false
      setPocContent('')
      setIsEditing(false)
      setError('')
      setIsGenerating(false)
      setIsSaving(false)
      setIsSubmitting(false)
    }
  }, [open])

  const generatePoC = async () => {
    // Prevent duplicate calls
    if (isGeneratingRef.current) {
      return
    }

    // Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new AbortController for this request
    const abortController = new AbortController()
    abortControllerRef.current = abortController
    isGeneratingRef.current = true

    setIsGenerating(true)
    setError('')

    try {
      const sessionId = session._id || session.id
      if (!sessionId) {
        throw new Error('Session ID is required')
      }

      const response = await pocApi.generatePoC(
        {
          noteType: 'poc',
          therapistSummary: null,
          contractId: contractId,
          sessionID: sessionId,
        },
        { signal: abortController.signal }
      )

      // Check if request was aborted
      if (abortController.signal.aborted) {
        return
      }

      const generatedContent = response.generatedNote || response.type || ''

      if (!generatedContent) {
        throw new Error('No content received from AI generation')
      }

      // Store as markdown (AI returns markdown)
      setPocContent(generatedContent)
    } catch (err: any) {
      // Don't show error if request was aborted
      if (
        abortController.signal.aborted ||
        err.name === 'AbortError' ||
        err.code === 'ERR_CANCELED' ||
        err.message === 'canceled'
      ) {
        return
      }
      // Check if it's the specific 404 error about initial evaluation report
      const errorMessage = err.response?.data?.message || err.message || ''
      const statusCode = err.response?.status || err.statusCode

      if (
        statusCode === 404 &&
        (errorMessage.includes('initial evaluation report') ||
          errorMessage.includes('Initial evaluation report'))
      ) {
        // Handle the specific error gracefully - show snackbar via callback
        setIsGenerating(false)
        if (onError) {
          onError(errorMessage)
          // onError callback will handle closing the dialog
          return
        } else {
          // Fallback: show error in dialog if no callback provided
          setError(errorMessage)
        }
        return
      }

      // Check if it's a timeout error
      if (
        err.code === 'ECONNABORTED' ||
        err.message?.includes('timeout') ||
        err.message?.includes('Timeout')
      ) {
        setError(
          'AI PoC generation is taking longer than expected. Please try again.'
        )
      } else {
        setError(
          errorMessage || 'Failed to generate PoC with AI. Please try again.'
        )
      }
    } finally {
      // Only reset if this is still the current request
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null
        isGeneratingRef.current = false
        setIsGenerating(false)
      }
    }
  }

  const handleSaveDraft = async () => {
    if (!pocContent.trim()) {
      setError('PoC content is required')
      return
    }

    setIsSaving(true)
    setError('')

    try {
      const sessionId = session._id || session.id
      if (!sessionId) {
        throw new Error('Session ID is required')
      }

      // Get HTML content from contentEditable if editing, otherwise convert markdown
      let pocHtml: string
      if (isEditing && pocInputRef.current) {
        pocHtml = pocInputRef.current.innerHTML || ''
      } else {
        pocHtml = markdownToHtml(pocContent)
      }

      let response;
      // If editing existing PoC, use edit API
      if (existingPoC) {
        response = await pocApi.editPoC({
          poc_id: existingPoC._id,
          poc_status: 'draft',
          poc_doc: pocHtml,
          signature: signatureData
        })
      } else {
        // Otherwise, create new PoC
        response = await pocApi.savePoC({
          contract_id: contractId,
          session_id: sessionId,
          poc_status: 'draft',
          poc_doc: pocHtml,
          signature: signatureData
        })
      }

      onSuccess(response?.message)
      onClose()
    } catch (err: any) {
      setError(
        err.response?.data?.message || err.message || 'Failed to save PoC draft'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleSubmitForCertification = async () => {
    if (!pocContent.trim()) {
      setError('PoC content is required')
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      const sessionId = session._id || session.id
      if (!sessionId) {
        throw new Error('Session ID is required')
      }

      // Get HTML content from contentEditable if editing, otherwise convert markdown
      let pocHtml: string
      if (isEditing && pocInputRef.current) {
        pocHtml = pocInputRef.current.innerHTML || ''
      } else {
        pocHtml = markdownToHtml(pocContent)
      }

      let response;
      // If editing existing PoC, use edit API
      if (existingPoC) {
        response = await pocApi.editPoC({
          poc_id: existingPoC._id,
          poc_status: 'submitted',
          poc_doc: pocHtml,
          signature: signatureData
        })
      } else {
        // Otherwise, create new PoC
        // Save the PoC as submitted
        // Note: Certification is done by admin separately, not by therapist
        response = await pocApi.savePoC({
          contract_id: contractId,
          session_id: sessionId,
          poc_status: 'submitted',
          poc_doc: pocHtml,
          signature: signatureData
        })
      }

      onSuccess(response?.message)
      onClose()
    } catch (err: any) {
      // Check for 403 error specifically
      if (err.response?.status === 403) {
        setError(
          err.response?.data?.message ||
          'You do not have permission to perform this action. Please contact an administrator.'
        )
      } else {
        setError(
          err.response?.data?.message ||
          err.message ||
          'Failed to submit PoC for certification'
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isGenerating && !isSaving && !isSubmitting) {
      onClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1.5}>
            <DescriptionIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Generate Plan of Care
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            disabled={isGenerating}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        {contract && (
          <NoteDemographicsHeader
            contract={contract}
            selectedCategoryTitle="Plan of Care"
          />
        )}
        {error && (
          <Fade in>
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          </Fade>
        )}

        {isGenerating ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={8}
          >
            <CircularProgress size={48} sx={{ color: '#2E7DAF', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Generating Plan of Care...
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              AI is analyzing session data and creating your plan
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* Edit Toggle */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Plan of Care Content
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setIsEditing(!isEditing)}
                disabled={isSaving || isSubmitting}
              >
                {isEditing ? 'View' : 'Edit'}
              </Button>
            </Box>

            {/* Content Display/Edit */}
            {isEditing ? (
              <Box
                ref={pocInputRef}
                contentEditable={!isSaving && !isSubmitting}
                suppressContentEditableWarning
                onFocus={() => {
                  isUserEditingRef.current = true
                }}
                onBlur={event => {
                  isUserEditingRef.current = false
                  const target = event.currentTarget as HTMLDivElement
                  // Capture HTML and convert to markdown for storage
                  const htmlContent = target.innerHTML || ''
                  const markdownContent = htmlToMarkdown(htmlContent)
                  setPocContent(markdownContent)
                }}
                onInput={event => {
                  const target = event.currentTarget as HTMLDivElement
                  // Capture HTML and convert to markdown for storage
                  const htmlContent = target.innerHTML || ''
                  const markdownContent = htmlToMarkdown(htmlContent)
                  setPocContent(markdownContent)
                }}
                data-placeholder="Plan of Care content will appear here..."
                sx={{
                  minHeight: 400,
                  maxHeight: 500,
                  overflowY: 'auto',
                  backgroundColor: 'white',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  padding: 2,
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontSize: '0.95rem',
                  lineHeight: 1.7,
                  color: 'text.primary',
                  '&:empty:before': {
                    content: 'attr(data-placeholder)',
                    color: 'text.secondary',
                  },
                  '& h1, & h2, & h3, & h4': {
                    margin: '0 0 0.75rem',
                    fontWeight: 600,
                  },
                  '& p': { margin: '0 0 0.75rem' },
                  '& ul': {
                    paddingLeft: '1.5rem',
                    margin: '0 0 0.75rem',
                    listStyleType: 'disc',
                  },
                  '& ol': {
                    paddingLeft: '1.5rem',
                    margin: '0 0 0.75rem',
                    listStyleType: 'decimal',
                  },
                  '& li': {
                    marginBottom: '0.5rem',
                    display: 'list-item',
                    listStylePosition: 'outside',
                  },
                  '& ul > li': {
                    listStyleType: 'disc',
                  },
                  '& ol > li': {
                    listStyleType: 'decimal',
                  },
                  '& strong': { fontWeight: 600 },
                }}
              />
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  maxHeight: '500px',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'grey.100',
                    borderRadius: 1,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'grey.400',
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'grey.500',
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    fontSize: '0.95rem',
                    lineHeight: 1.7,
                    color: 'text.primary',
                    '& h1, & h2, & h3, & h4': {
                      margin: '0 0 0.75rem',
                      fontWeight: 600,
                    },
                    '& p': { margin: '0 0 0.75rem' },
                    '& ul': {
                      paddingLeft: '1.5rem',
                      margin: '0 0 0.75rem',
                      listStyleType: 'disc',
                    },
                    '& ol': {
                      paddingLeft: '1.5rem',
                      margin: '0 0 0.75rem',
                      listStyleType: 'decimal',
                    },
                    '& li': {
                      marginBottom: '0.5rem',
                      display: 'list-item',
                      listStylePosition: 'outside',
                    },
                    '& ul > li': {
                      listStyleType: 'disc',
                    },
                    '& ol > li': {
                      listStyleType: 'decimal',
                    },
                    '& strong': { fontWeight: 600 },
                  }}
                  dangerouslySetInnerHTML={{ __html: renderedPocHtml }}
                />
              </Paper>
            )}
          </Stack>
        )}

        {/* Therapist Signature Section */}
        {!isGenerating && pocContent && (
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}
            >
              Therapist Signature (draw below){' '}
              <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <Box
              onMouseUp={handleSignatureEnd}
              onTouchEnd={handleSignatureEnd}
              onMouseLeave={handleSignatureEnd}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'grey.50',
                mb: 1.5,
                overflow: 'hidden',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
            >
              <SignatureCanvas
                ref={signaturePadRef}
                canvasProps={{
                  width: 800,
                  height: 150,
                  className: 'signature-canvas',
                  style: { maxWidth: '100%', height: 'auto', minHeight: '150px' },
                }}
                onEnd={handleSignatureEnd}
              />
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={handleClearSignature}
              sx={{ textTransform: 'none' }}
            >
              Clear Signature
            </Button>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {!isGenerating && pocContent && (
          <>
            <Button
              onClick={handleSaveDraft}
              disabled={isSaving || isSubmitting || !pocContent.trim()}
              variant="outlined"
              startIcon={
                isSaving ? <CircularProgress size={16} /> : <SaveIcon />
              }
              sx={{
                backgroundColor: 'grey.100',
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'grey.200',
                },
              }}
            >
              Save as Draft
            </Button>
            <Button
              onClick={handleSubmitForCertification}
              disabled={isSaving || isSubmitting || !pocContent.trim() || !signatureData}
              variant="contained"
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckCircleIcon />
                )
              }
              sx={{
                backgroundColor: '#2E7DAF',
                '&:hover': {
                  backgroundColor: '#2563a0',
                },
              }}
            >
              Submit for Certification
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default PoCGenerationDialog
